import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Router } from '@angular/router';
import { State, SignalButton } from '@vallift/ngl';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import { WorkspaceCardComponent } from '../workspace-card/workspace-card.component';
import {
  navigateAfterLogin,
  switchToWorkspace,
} from '@core/utils/auth-routing.util';
import {
  groupWorkspacesByCompany,
  type WorkspaceCompanyGroup,
} from '@core/utils/workspace-groups.util';
import type {
  ListMyWorkspacesApiResponse,
  MyWorkspaceListItemResponse,
} from '@core/api/workspace/models';

export interface WorkspaceSwitcherOverlayData {
  currentWorkspaceId?: string;
}

@Component({
  selector: 'app-workspace-switcher-overlay',
  imports: [WorkspaceCardComponent, SignalButton],
  templateUrl: './workspace-switcher-overlay.html',
  styleUrl: './workspace-switcher-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceSwitcherOverlayComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(DialogRef<boolean>);
  private readonly dialogData = inject<WorkspaceSwitcherOverlayData>(DIALOG_DATA, {
    optional: true,
  });

  readonly workspacesState = new State<ListMyWorkspacesApiResponse>();
  readonly switchingWorkspaceId = signal<string | null>(null);

  ngOnInit(): void {
    this.loadWorkspaces();
  }

  currentWorkspaceId(): string | undefined {
    return this.dialogData?.currentWorkspaceId ?? this.tokenStorage.getWorkspace()?.id;
  }

  workspaceGroups(): WorkspaceCompanyGroup[] {
    return groupWorkspacesByCompany(
      this.workspacesState.response()?.data?.workspaces ?? [],
    );
  }

  close(): void {
    this.dialogRef.close(false);
  }

  onCreateWorkspace(): void {
    this.dialogRef.close(false);
    void this.router.navigate(['/create-workspace']);
  }

  async onWorkspaceSelected(workspace: MyWorkspaceListItemResponse): Promise<void> {
    if (workspace.id === this.currentWorkspaceId() || this.switchingWorkspaceId()) {
      this.close();
      return;
    }

    this.switchingWorkspaceId.set(workspace.id);

    try {
      const authData = await switchToWorkspace(
        this.userAPIService,
        this.tokenStorage,
        workspace.id,
      );

      await navigateAfterLogin(this.router, { workspace: authData.workspace });
      this.dialogRef.close(true);
    } catch {
      this.switchingWorkspaceId.set(null);
    }
  }

  private loadWorkspaces(): void {
    this.workspacesState.notifyLoading();
    this.userAPIService.listMyWorkspaces().subscribe({
      next: (response) => this.workspacesState.notifySuccess(response),
      error: () => this.workspacesState.notifyError('Failed to load workspaces.'),
    });
  }
}
